import { useEffect, useState, useCallback, useRef, Fragment } from "react";
import { OrderSource, SOURCE_OPTIONS } from "@/components/OrderSource";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  RefreshCw, Package, TrendingUp, Clock, Search, AlertCircle,
  CheckCircle2, X, Filter, Calendar, FileText, Truck, ChevronDown, ChevronUp, ImageOff, ExternalLink, FilePlus, TruckIcon,
  Bookmark, BookmarkPlus, Save, Trash2, SlidersHorizontal, Ban, ChevronLeft, ChevronRight,
  Plus, Download, CheckSquare,
} from "lucide-react";
import OrderDetailModal from "./OrderDetailModal";
import InvoicePreviewModal from "./InvoicePreviewModal";
import AwbGeneratorModal from "./AwbGeneratorModal";
import ManualOrderModal from "./ManualOrderModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function fmtMoney(value, currency) {
  if (value == null || isNaN(value)) return "—";
  const c = (currency || "RON").toUpperCase();
  if (c === "HUF") return `${Math.round(value).toLocaleString("hu-HU")} HUF`;
  if (c === "EUR") return `€${Number(value).toFixed(2)}`;
  return `${Number(value).toFixed(2)} RON`;
}

function fmtVoucher(value, currency) {
  if (!value) return "—";
  const c = (currency || "RON").toUpperCase();
  const v = Math.abs(value);
  if (c === "HUF") return `−${Math.round(v).toLocaleString("hu-HU")} HUF`;
  if (c === "EUR") return `−€${v.toFixed(2)}`;
  return `−${v.toFixed(2)} RON`;
}

const STATUS_COLORS = {
  "Nouă": "bg-amber-500/10 text-amber-300 border-amber-500/30",
  "În procesare": "bg-blue-500/10 text-blue-300 border-blue-500/30",
  "Pregătită": "bg-violet-500/10 text-violet-300 border-violet-500/30",
  "Finalizată": "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  "Anulată": "bg-rose-500/10 text-rose-300 border-rose-500/30",
  "Returnată": "bg-orange-500/10 text-orange-300 border-orange-500/30",
  // Neridicată = comandă stornată din cauza refuzului la ridicare (user
  // request 2026-08-18). Distinct de Returnată = clientul a refuzat înainte
  // de a lua coletul (nu e retur RMA, e AWB returnat de curier).
  "Neridicată": "bg-fuchsia-500/10 text-fuchsia-300 border-fuchsia-500/30",
};
const STATUS_OPTIONS = [
  { value: 1, label: "Nouă" }, { value: 2, label: "În procesare" }, { value: 3, label: "Pregătită" },
  { value: 4, label: "Finalizată" }, { value: 0, label: "Anulată" }, { value: 5, label: "Returnată" },
];

const PLATFORM_LABEL = {
  emag_ro_fbe: "EMAG-RO-FBE",
  emag_ro_bau: "EMAG-RO-BAU",
  emag_bg_fbe: "EMAG-BG-FBE",
  emag_bg_bau: "EMAG-BG-BAU",
  emag_hu_fbe: "EMAG-HU-FBE",
  emag_hu_bau: "EMAG-HU-BAU",
  gomag_bragus: "BRAGUS.ro (Gomag)",
  shopify_bragus: "BRAGUS.ro (Shopify)",
  trendyol_ro: "TRENDYOL-RO",
  trendyol_bg: "TRENDYOL-BG",
  manual_bragus: "COMANDĂ MANUALĂ",
};
const PLATFORM_FLAG = {
  emag_ro_fbe: "🇷🇴", emag_ro_bau: "🇷🇴",
  emag_bg_fbe: "🇧🇬", emag_bg_bau: "🇧🇬",
  emag_hu_fbe: "🇭🇺", emag_hu_bau: "🇭🇺",
  gomag_bragus: "🇷🇴",
  shopify_bragus: "🇷🇴",
  trendyol_ro: "🇷🇴", trendyol_bg: "🇧🇬",
  manual_bragus: "📞",
};

const PLATFORM_LOGO = {
  emag_ro_fbe: "/emag-logo.png",
  emag_ro_bau: "/emag-logo.png",
  emag_bg_fbe: "/emag-logo.png",
  emag_bg_bau: "/emag-logo.png",
  emag_hu_fbe: "/emag-logo.png",
  emag_hu_bau: "/emag-logo.png",
  gomag_bragus: "/gomag-logo.png",
  shopify_bragus: "/shopify-logo.svg",
  trendyol_ro: "/trendyol-logo.jpg",
  trendyol_bg: "/trendyol-logo.jpg",
};

function StatCard({ icon: Icon, label, value, subtitle, accent, testId, onClick }) {
  const interactive = typeof onClick === "function";
  const Tag = interactive ? "button" : "div";
  return (
    <Tag
      data-testid={testId}
      onClick={onClick}
      type={interactive ? "button" : undefined}
      className={
        "w-full rounded-2xl border border-zinc-800 bg-zinc-950/70 p-5 text-left transition hover:border-zinc-700 " +
        (interactive ? "cursor-pointer focus:outline-none focus:ring-2 focus:ring-orange-500/30 hover:bg-zinc-900/70" : "")
      }
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-zinc-500">{label}</p>
          <p className="mt-2 text-3xl font-semibold text-zinc-50">{value}</p>
          {subtitle && <p className="mt-1 text-[11px] text-zinc-500">{subtitle}</p>}
        </div>
        <div className={`rounded-xl p-2.5 ${accent}`}><Icon className="h-5 w-5" /></div>
      </div>
    </Tag>
  );
}

function StatusBadge({ label }) {
  const cls = STATUS_COLORS[label] || "bg-zinc-500/10 text-zinc-300 border-zinc-500/30";
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-medium ${cls}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" />{label}
    </span>
  );
}

// Shopify fraud risk pill — 3 culori, afisat sub numar comanda pentru
// comenzile Shopify. Tras din Shopify Admin GraphQL (OrderRiskSummary) la
// sync-time si cache-uit pe doc (`shopify_risk.level`).
const RISK_LABEL = { low: "Scăzut", medium: "Mediu", high: "Ridicat" };
const RISK_CLS = {
  low:    "border-emerald-500/40 bg-emerald-500/15 text-emerald-200",
  medium: "border-amber-500/40 bg-amber-500/15 text-amber-200",
  high:   "border-rose-500/40 bg-rose-500/15 text-rose-200",
};
function ShopifyRiskPill({ order }) {
  const ik = (order?._integration_key || "").toLowerCase();
  if (!ik.startsWith("shopify")) return null;
  const risk = order?.shopify_risk || {};
  const level = (risk.level || "").toLowerCase();
  if (!RISK_LABEL[level]) return null;
  const facts = Array.isArray(risk.facts) ? risk.facts : [];
  const tooltip = facts.length
    ? facts.slice(0, 5).map((f) => `• ${f.description || ""}`).join("\n")
    : `Risc comandă Shopify: ${RISK_LABEL[level]}`;
  return (
    <span
      data-testid={`shopify-risk-${order.id}`}
      title={tooltip}
      className={`mt-0.5 inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${RISK_CLS[level]}`}
    >
      <span className="h-1 w-1 rounded-full bg-current" />
      Risc: {RISK_LABEL[level]}
    </span>
  );
}


function PlatformBadge({ orderKey, isGenius, source, orderId }) {
  const key = orderKey || "emag_ro_fbe";
  const label = PLATFORM_LABEL[key] || key.toUpperCase();
  const flag = PLATFORM_FLAG[key] || "";
  const logo = PLATFORM_LOGO[key];
  // Compact stacked layout: [logo + flag + label] on row 1, [Genius] below.
  // The logos already have transparent / dark backgrounds so they sit
  // naturally on the dark UI without extra styling.
  return (
    <div className="flex flex-col items-start gap-1" data-testid={`platform-badge-${key}`}>
      <div className="flex items-center gap-1.5">
        {logo && (
          <img src={logo} alt={label} className="h-5 w-auto max-w-[44px] object-contain"
               data-testid={`platform-logo-${key}`} />
        )}
        <span className="text-[12px] leading-none">{flag}</span>
        <span className="font-mono text-[10px] font-semibold tracking-tight text-zinc-300">{label}</span>
      </div>
      {isGenius && (
        <img
          src="/emag-genius.svg"
          alt="eMAG Genius"
          title="Comandă Genius"
          data-testid="genius-badge"
          className="h-3.5 w-auto opacity-90"
        />
      )}
      <OrderSource src={source} testId={`order-source-${orderId}`} />
    </div>
  );
}

function Banner({ kind, message, onClose }) {
  if (!message) return null;
  const styles = {
    error: "border-rose-500/40 bg-rose-500/10 text-rose-200",
    success: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200",
  }[kind || "info"];
  const Icon = kind === "error" ? AlertCircle : CheckCircle2;
  return (
    <div data-testid={`banner-${kind}`} className={`flex items-start gap-3 rounded-xl border px-4 py-3 text-sm ${styles}`}>
      <Icon className="mt-0.5 h-4 w-4 flex-shrink-0" />
      <div className="flex-1 whitespace-pre-wrap">{message}</div>
      {onClose && <button data-testid="banner-close" onClick={onClose} className="rounded p-0.5 opacity-70 hover:opacity-100"><X className="h-4 w-4" /></button>}
    </div>
  );
}

function ProductThumb({ src, alt, size = "md" }) {
  const url = src ? (src.startsWith("/api/") ? `${BACKEND_URL}${src}` : src) : null;
  const dim = size === "lg" ? "h-16 w-16" : "h-14 w-14";
  return (
    <div className={`${dim} flex-shrink-0 overflow-hidden rounded-md border border-zinc-800 bg-zinc-900`}>
      {url ? (
        <img src={url} alt={alt || ""} className="h-full w-full object-cover" loading="lazy" />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-zinc-700"><ImageOff className="h-5 w-5" /></div>
      )}
    </div>
  );
}

// Expand the products array so each unit appears as its own entry. A line
// with quantity=3 becomes 3 identical entries — so „2 buc same SKU" → 2 cards
// and „1 buc + 1 buc different SKUs" → still 2 cards. UX requirement: the
// thumbnail strip and the dropdown should both reflect the TOTAL number of
// items shipped, not just distinct SKUs.
function expandByQuantity(products) {
  const out = [];
  for (const p of (products || [])) {
    const q = Math.max(1, parseInt(p.quantity, 10) || 1);
    for (let i = 0; i < q; i++) out.push(p);
  }
  return out;
}

function ProductsCell({ order, isOpen, onToggle }) {
  const products = expandByQuantity(order.products);
  const count = products.length;
  const label = count === 1 ? "1 produs" : `${count} produse`;
  return (
    <div>
      <div className="flex items-center gap-1">
        {products.slice(0, 3).map((p, i) => <ProductThumb key={i} src={p.image} alt={p.name} />)}
        {products.length > 3 && (
          <span className="rounded-md border border-zinc-800 bg-zinc-900 px-1.5 py-1 text-[10px] text-zinc-400">+{products.length - 3}</span>
        )}
      </div>
      <button
        data-testid={`products-toggle-${order.id}`}
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        className="mt-1.5 inline-flex items-center gap-1 rounded-md text-[11px] font-medium text-orange-300 hover:text-orange-200"
      >
        {label}
        {isOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </button>
    </div>
  );
}

function PaymentCell({ order }) {
  // Format: top line = method ("Online" / "Ramburs"), bottom line = state
  // ("Încasată" green or "Neplătită" red for Online; nothing/"Anulată" red).
  const mode = order.payment_mode_id;
  const statusName = (order.status_name || "").toLowerCase();
  if (statusName === "anulata" || statusName === "anulată" || Number(order.status) === 0) {
    return <span className="inline-flex rounded-md border border-rose-500/40 bg-rose-500/10 px-2 py-0.5 text-[10px] font-medium text-rose-300">Anulată</span>;
  }
  if (mode === 3) {
    const paid = Number(order.payment_status) === 1 || Number(order.cashed_co || 0) > 0;
    return (
      <div className="flex flex-col gap-0.5">
        <span className="text-[11px] font-medium text-zinc-200">Online</span>
        {paid
          ? <span className="text-[10px] font-medium text-emerald-400">Încasată</span>
          : <span className="text-[10px] font-medium text-rose-400">Neplătită</span>}
      </div>
    );
  }
  if (mode === 1) {
    // Bragus Gomag/Manual COD orders — statusul de încasare (Ramburs Sameday
    // + Online Netopia) s-a mutat conceptual în Financiar → „Status Încasare
    // Comenzi" (2026-02-04). Aici afișăm doar metoda de plată.
    return <span className="text-[11px] text-zinc-300">Ramburs</span>;
  }
  return <span className="text-[10px] text-zinc-500">{order.payment_mode || "—"}</span>;
}

function DeliveryCell({ order }) {
  const m = (order.delivery_mode || "").toLowerCase();
  let label = order.delivery_mode || "—";
  const isLocker = m === "pickup" || m === "easybox" || m.includes("locker") || m.includes("easybox") || m.includes("ridicare") || m.includes("magazin");
  if (isLocker) {
    label = "Locker";
  } else if (m === "courier" || m === "home" || m.includes("domic") || m.includes("curier") || m.includes("livrare")) {
    label = "Domiciliu";
  }
  // Shopify + locker: the customer picks the specific easybox on the Thank You
  // page (or from the customer-account order page). Surface the selection state
  // right in the list so operators know at a glance which orders are waiting
  // on the customer before we can generate the AWB. User request 2026-02-27.
  const isShopify = (order._integration_key || "").startsWith("shopify");
  if (isShopify && isLocker) {
    // `locker_id === "?"` is a backend sentinel the Shopify normalizer sets
    // when the shipping method is tagged as locker/easybox but the customer
    // has NOT yet picked a specific one (metafield empty). Treat it as
    // unselected in the badge (user raport 2026-10-05, order #1024).
    const rawId = order.locker_id || order.customer_locker_id;
    const hasLocker = !!(rawId && String(rawId).trim() && String(rawId).trim() !== "?");
    return (
      <div className="flex flex-col gap-0.5">
        <span className="text-[11px] text-zinc-300">{label}</span>
        <span
          data-testid={`locker-state-${order.id}`}
          className={hasLocker
            ? "inline-flex w-fit items-center rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-300 ring-1 ring-emerald-500/30"
            : "inline-flex w-fit items-center rounded-full bg-rose-500/15 px-2 py-0.5 text-[10px] font-semibold text-rose-300 ring-1 ring-rose-500/30"}
          title={hasLocker
            ? `Locker selectat: ${order.locker_name || order.locker_id}`
            : "Clientul nu a ales lockerul încă — AWB blocat"}
        >
          {hasLocker ? "Selectat" : "Neselectat"}
        </span>
      </div>
    );
  }
  return <span className="text-[11px] text-zinc-300">{label}</span>;
}

function PaginationFooter({ total, page, setPage, pageSize, setPageSize, top = false }) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, pageCount);
  const from = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const to = Math.min(safePage * pageSize, total);
  const border = top ? "border-b" : "border-t";
  return (
    <div className={`relative flex flex-wrap items-center gap-3 ${border} border-zinc-800 px-4 py-2.5 text-xs text-zinc-400`} data-testid="orders-pagination">
      {/* Left: total + page size */}
      <div className="flex items-center gap-2">
        <span>
          {total === 0
            ? "Nicio comandă"
            : <>Afișează <span className="font-medium text-zinc-200">{from}–{to}</span> din <span className="font-medium text-zinc-200">{total}</span></>}
        </span>
        <label className="ml-3 inline-flex items-center gap-1.5">
          <span className="text-zinc-500">pe pagină:</span>
          <select
            data-testid="orders-page-size"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="rounded border border-zinc-800 bg-zinc-950 px-1.5 py-1 text-xs text-zinc-200 focus:border-orange-500/50 focus:outline-none"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </label>
      </div>
      {/* Center: navigation buttons (clear of the bottom-right Emergent overlay) */}
      <div className="mx-auto flex items-center gap-1">
        <button data-testid="orders-page-prev" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}
          className="inline-flex h-7 items-center gap-1 rounded border border-zinc-800 bg-zinc-900 px-2 text-xs text-zinc-300 hover:text-zinc-100 disabled:opacity-40">
          <ChevronLeft className="h-3.5 w-3.5" /> Înapoi
        </button>
        <span className="px-2 text-xs text-zinc-400" data-testid="orders-page-info">Pag. <span className="font-mono text-zinc-100">{safePage}</span> / {pageCount}</span>
        <button data-testid="orders-page-next" disabled={safePage >= pageCount} onClick={() => setPage(safePage + 1)}
          className="inline-flex h-7 items-center gap-1 rounded border border-zinc-800 bg-zinc-900 px-2 text-xs text-zinc-300 hover:text-zinc-100 disabled:opacity-40">
          Înainte <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function VoucherCell({ order }) {
  const v = order.vouchers || [];
  if (!v.length) return <span className="text-zinc-700">—</span>;
  // Support both voucher formats:
  //   • eMAG  → { sale_price (negative net), sale_price_vat (negative VAT) }
  //   • Gomag → { value (POSITIVE gross — already with VAT) }
  let total = 0;
  for (const x of v) {
    if (x.value !== undefined && x.value !== null && x.sale_price === undefined) {
      // Gomag: positive gross → display as negative deduction
      total += -Math.abs(Number(x.value || 0));
    } else {
      total += Number(x.sale_price || 0) + Number(x.sale_price_vat || 0);
    }
  }
  if (total === 0) return <span className="text-zinc-700">—</span>;
  return <span className="font-semibold text-rose-400">{fmtVoucher(total, order.currency)}</span>;
}

// Returns invoice/awb URL from order.attachments if present (eMAG type 1=invoice, 3=awb)
function findAttachment(order, type) {
  const att = order.attachments || [];
  for (const a of att) {
    if (Number(a.type) === type && a.url) return a.url;
  }
  return null;
}

function InvoiceCell({ order, onCreate, onStorno, busyId }) {
  const url = order.invoice_url || findAttachment(order, 1);
  const stornoUrl = order.storno_url;
  // When a storno was issued for this order, the original invoice is logically
  // cancelled — we hide its "Deschide" button and show "Creează" again so the
  // user can issue a fresh invoice. The storno itself stays accessible via the
  // red "S" badge next to it.
  const showOriginalAsCreated = !!url && !stornoUrl;

  return (
    <div className="inline-flex items-center gap-1">
      {showOriginalAsCreated ? (
        <a data-testid={`invoice-open-${order.id}`} href={url} target="_blank" rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[10px] font-medium text-emerald-200 hover:bg-emerald-500/20">
          <ExternalLink className="h-3 w-3" /> Deschide
        </a>
      ) : (
        <button data-testid={`invoice-create-${order.id}`} onClick={(e) => { e.stopPropagation(); onCreate(order); }} disabled={busyId === order.id}
          className="inline-flex items-center gap-1 rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1 text-[10px] font-medium text-zinc-300 hover:border-orange-500/40 hover:text-orange-200 disabled:opacity-50">
          <FilePlus className="h-3 w-3" /> Creează
        </button>
      )}
      {stornoUrl ? (
        <a data-testid={`storno-open-${order.id}`} href={stornoUrl} target="_blank" rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          title={`Storno: ${order.storno_number || ''}`}
          className="inline-flex items-center justify-center rounded-md border border-rose-500/40 bg-rose-500/10 px-1.5 py-1 text-[10px] font-bold text-rose-200 hover:bg-rose-500/20">
          S
        </a>
      ) : url ? (
        // Storno button only shown if the original invoice exists (you can't
        // storno an invoice that isn't there yet).
        <button data-testid={`storno-create-${order.id}`} onClick={(e) => { e.stopPropagation(); onStorno?.(order); }} disabled={busyId === order.id}
          title="Emite factură storno"
          className="inline-flex items-center justify-center rounded-md border border-zinc-700 bg-zinc-900 px-1.5 py-1 text-[10px] font-bold text-zinc-400 hover:border-rose-500/40 hover:text-rose-300 disabled:opacity-50">
          S
        </button>
      ) : null}
    </div>
  );
}

function AwbStatusPill({ order }) {
  // Feb 2026: read awb_state directly from the already-fetched order so the
  // pill renders in-sync with the row (no more N+1 fetch causing the
  // several-seconds delay users complained about). Backend fresh-refreshes
  // via the 5-min background loop + inline poll in `/api/orders`.
  const state = order?.awb_state;
  const raw = order?.awb_state_raw || "";
  if (!state) return null;
  const STATES = {
    ready:            { label: "Ready",     cls: "border-amber-500/40 bg-amber-500/10 text-amber-200" },
    on_route:         { label: "On Route",  cls: "border-sky-500/40 bg-sky-500/10 text-sky-200" },
    delivered:        { label: "Delivered", cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200" },
    return:           { label: "Return",    cls: "border-rose-500/50 bg-rose-500/15 text-rose-200" },
    // Distinct color from delivered — user cerință Feb 2026. Coletul e la noi.
    return_delivered: { label: "Returned",  cls: "border-orange-500/50 bg-orange-500/15 text-orange-200" },
    cancelled:        { label: "Cancelled", cls: "border-rose-500/40 bg-rose-500/10 text-rose-200" },
  };
  const cur = STATES[state];
  if (!cur) return null;
  return (
    <span data-testid={`awb-status-pill-${order.id}`} title={raw}
      className={`mt-1 inline-flex items-center rounded-md border px-1.5 py-[1px] text-[9px] font-semibold uppercase tracking-wider ${cur.cls}`}>
      {cur.label}
    </span>
  );
}

function AWBCell({ order, onCreate, busyId }) {
  // Trendyol orders: AWB is generated automatically by Trendyol via FanCourier
  // (RO) or DPD (BG). We have a `awb_number` from the API and a backend
  // endpoint that streams the PDF label in A6.
  if ((order._integration_key || "").startsWith("trendyol_") && order.awb_number) {
    const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/trendyol/orders/${order._composite_id || order._emag_id || order.id}/awb`;
    return (
      <div className="flex flex-col items-start gap-0">
        <a data-testid={`awb-trendyol-${order.id}`} href={pdfUrl} target="_blank" rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[10px] font-medium text-emerald-200 hover:bg-emerald-500/20">
          <ExternalLink className="h-3 w-3" /> Printează AWB
        </a>
        <span className="text-[9px] text-zinc-500 mt-0.5 font-mono">{order.awb_number} · {order.awb_provider_name || ""}</span>
      </div>
    );
  }
  // AWB Open button must show ONLY when we actually have an AWB URL.
  // Earlier code fell back to `findAttachment(order, 3)`, but eMAG
  // attachment type 3 is the WARRANTY PDF — so the AWB cell was wrongly
  // opening the warranty document.
  const url = order.awb_url;
  if (url) {
    return (
      <div className="flex flex-col items-start gap-0">
        <a data-testid={`awb-open-${order.id}`} href={url} target="_blank" rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[10px] font-medium text-emerald-200 hover:bg-emerald-500/20">
          <ExternalLink className="h-3 w-3" /> Deschide
        </a>
        <AwbStatusPill order={order} />
      </div>
    );
  }
  return (
    <button data-testid={`awb-create-${order.id}`} onClick={(e) => { e.stopPropagation(); onCreate(order); }} disabled={busyId === order.id}
      className="inline-flex items-center gap-1 rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1 text-[10px] font-medium text-zinc-300 hover:border-sky-500/40 hover:text-sky-200 disabled:opacity-50">
      <Truck className="h-3 w-3" /> Emite AWB
    </button>
  );
}

function ProductsDropdown({ order }) {
  // Show one card per unit (a quantity=2 product = 2 cards) — matches the
  // expanded count shown in the collapsed cell, so the click result is
  // consistent with the badge.
  const products = expandByQuantity(order.products);
  // eMAG sends `reason_cancellation: { id, name }` on cancelled orders.
  // We surface it inline in the expanded view so the operator sees WHY
  // the order was cancelled without opening the detail modal.
  const rc = order.reason_cancellation;
  const cancelReason = rc && typeof rc === "object"
    ? `${rc.name || ""}${rc.id ? ` (#${rc.id})` : ""}`.trim()
    : (typeof rc === "string" ? rc : null);
  return (
    <tr data-testid={`products-dropdown-${order.id}`} className="bg-zinc-900/40">
      <td className="w-8 px-2"></td>
      <td colSpan={12} className="px-4 py-3">
        {cancelReason && (
          <div
            data-testid={`cancel-reason-${order.id}`}
            className="mb-3 flex items-start gap-2 rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200"
          >
            <Ban className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <div>
              <span className="font-semibold">Motiv anulare eMAG:</span> {cancelReason}
            </div>
          </div>
        )}
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p, i) => (
            <div key={i} className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900/60 p-2">
              <ProductThumb src={p.image} alt={p.name} size="lg" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium text-zinc-100" title={p.name}>{p.name}</p>
                <p className="text-[10px] text-zinc-500 font-mono">SKU: {p.ext_part_number || p.part_number || `#${p.product_id}`}</p>
                <p className="text-[10px] text-zinc-500">{fmtMoney(Number(p.sale_price) * (1 + Number(p.vat || 0)), order.currency)}</p>
              </div>
            </div>
          ))}
        </div>
      </td>
    </tr>
  );
}

const PLATFORM_KEYS = ["emag_ro_fbe", "emag_ro_bau", "emag_bg_fbe", "emag_bg_bau", "emag_hu_fbe", "emag_hu_bau", "gomag_bragus", "shopify_bragus", "trendyol_ro", "trendyol_bg", "manual_bragus"];

function AdvInput({ testId, label, value, onChange, placeholder, hint }) {
  return (
    <div>
      <label className="block text-[10px] font-medium uppercase tracking-wider text-zinc-500">{label}</label>
      <input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-100 placeholder-zinc-600 outline-none focus:border-orange-500/40" />
      {hint && <p className="mt-1 text-[9px] text-zinc-600">{hint}</p>}
    </div>
  );
}

const EMPTY_FILTERS = {
  // No implicit date window — the "Ultimele X zile" dropdown was removed
  // (user request 2026-02-04). Use a big lookback so backend returns
  // everything; users refine via date_from / date_to manually.
  days: 3650,
  search: "",
  platforms: [], // multi
  statuses: [],  // multi
  customer_name: "",
  order_id: "",
  sku: "",
  phone: "",
  date_from: "",
  date_to: "",
  awb: "",
  invoice_id: "",
  invoiced: "all", // 'yes' | 'no' | 'all'
  genius: "all",   // 'yes' | 'no' | 'all' — eMAG Genius membership
  source: "all",   // Shopify sursa prima vizită: Google | Meta | Email | Direct | Altele
  payment: "all",  // 'online' | 'ramburs' | 'all'
  cod_refund: "all", // 'paid' | 'unpaid' | 'unpaid_over_10d' | 'all'
  awb_state: "",   // '', 'return', 'ready', 'on_route', 'delivered', 'cancelled'
  awb_downloaded: "all", // 'all' | 'yes' (cu AWB) | 'no' (fără AWB, exclude FBE)
  printed_today: "all",  // 'all' | 'yes' (AWB printat azi)
  exclude_trendyol_prepared: "all", // 'all' | 'yes' — exclude Trendyol status=3 (Pregătită)
};

// ---------------------------------------------------------------------------
// ExportOrdersModal — pick which columns to include in the CSV export. Reuses
// the table's current filters automatically (the parent re-uses
// `buildQueryParams`). Defaults to ALL 4 columns checked.
// ---------------------------------------------------------------------------
const EXPORT_COLUMNS = [
  { key: "customer_name", label: "Nume client" },
  { key: "phone",         label: "Telefon" },
  { key: "order_id",      label: "Nr comandă" },
  { key: "product_link",  label: "Link produs (din PNK)" },
  { key: "sku",           label: "SKU produs" },
  { key: "order_date",    label: "Data comenzii" },
];

function ExportOrdersModal({ open, onClose, onExport, exporting }) {
  const [picked, setPicked] = useState(() => EXPORT_COLUMNS.map((c) => c.key));
  if (!open) return null;
  const toggle = (k) => setPicked((p) => p.includes(k) ? p.filter((x) => x !== k) : [...p, k]);
  const allChecked = picked.length === EXPORT_COLUMNS.length;
  const toggleAll = () => setPicked(allChecked ? [] : EXPORT_COLUMNS.map((c) => c.key));
  const submit = () => {
    if (!picked.length) return;
    onExport(picked);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose} data-testid="export-modal">
      <div className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-950 p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-zinc-100">Exportă comenzile filtrate</h3>
            <p className="mt-1 text-xs text-zinc-400">Se vor exporta comenzile așa cum sunt filtrate ACUM în tabel.<br/>Alege ce coloane să apară în Excel:</p>
          </div>
          <button onClick={onClose} className="rounded-md p-1 text-zinc-500 hover:bg-zinc-900 hover:text-zinc-200" aria-label="Închide">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mb-4 rounded-lg border border-zinc-800 bg-zinc-900/50 p-3">
          <label className="flex cursor-pointer items-center gap-2 border-b border-zinc-800 pb-2 mb-2">
            <input
              type="checkbox" checked={allChecked} onChange={toggleAll}
              data-testid="export-toggle-all"
              className="h-4 w-4 accent-orange-500"
            />
            <span className="text-xs uppercase tracking-wider font-semibold text-zinc-300">Toate coloanele</span>
          </label>
          <div className="space-y-2">
            {EXPORT_COLUMNS.map((c) => (
              <label key={c.key} className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 hover:bg-zinc-900/60">
                <input
                  type="checkbox" checked={picked.includes(c.key)}
                  onChange={() => toggle(c.key)}
                  data-testid={`export-col-${c.key}`}
                  className="h-4 w-4 accent-orange-500"
                />
                <span className="text-sm text-zinc-200">{c.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2">
          <button
            onClick={onClose} disabled={exporting}
            data-testid="export-cancel"
            className="rounded-md border border-zinc-800 bg-zinc-900 px-4 py-2 text-sm text-zinc-300 hover:text-zinc-100 disabled:opacity-50"
          >Anulează</button>
          <button
            onClick={submit} disabled={exporting || picked.length === 0}
            data-testid="export-confirm"
            className="inline-flex items-center gap-2 rounded-md border border-sky-500/50 bg-sky-500/10 px-4 py-2 text-sm font-medium text-sky-200 hover:bg-sky-500/20 disabled:opacity-50"
          >
            {exporting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {exporting ? "Se exportă..." : "Descarcă Excel"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// BulkPreviewModal — shown before executing the bulk AWB or bulk Invoice
// action. Lists one row per selected order: order id, customer name and
// (depending on `kind`) either the cash-on-delivery amount (AWB) or the
// total invoiceable amount (factură). Confirm → invokes the action's
// `onConfirm`. Cancel → just closes the modal.
// ---------------------------------------------------------------------------
function BulkPreviewModal({ preview, onCancel }) {
  if (!preview) return null;
  const { kind, targets, onConfirm } = preview;
  const isAwb = kind === "awb";
  const _fmt = (n, c) => {
    const num = Number(n || 0);
    return num.toLocaleString("ro-RO", { style: "currency", currency: c || "RON", minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  // Per-order amount the row should display.
  //   AWB → ramburs courier: `computed_total` if payment_mode is RAMBURS
  //         (mode_id=1 for eMAG, mode_code=1 for manual/Gomag), 0 for
  //         card-online (already paid). eMAG never sets `cashed_cod` until
  //         the courier collects, so we cannot use it for the preview.
  //   Invoice → total invoiceable amount = `computed_total`.
  //   Fix 2026-02-04: Manual Bragus orders use `payment_mode_code` (not
  //   `payment_mode_id`), so ramburs was showing as „card / 0".
  const amountFor = (o) => {
    const isRamburs = Number(o.payment_mode_id) === 1 || Number(o.payment_mode_code) === 1;
    if (isAwb) {
      return isRamburs
        ? Number(o.computed_total || o.total_amount || o.total || 0)
        : 0;
    }
    return Number(o.computed_total || o.total_amount || o.total || 0);
  };
  const sum = targets.reduce((acc, o) => acc + amountFor(o), 0);
  const currency = targets[0]?.currency || "RON";
  const displayId = (o) => {
    const k = o._integration_key || "";
    return ((k.startsWith("gomag") || k.startsWith("shopify")) && o._gomag_number)
      ? o._gomag_number
      : o.id;
  };
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onCancel}
      data-testid="bulk-preview-modal"
    >
      <div className="w-full max-w-2xl rounded-2xl border border-zinc-800 bg-zinc-950 p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-zinc-100">
              {isAwb ? "Confirmare generare AWB" : "Confirmare emitere facturi"}
            </h3>
            <p className="mt-1 text-xs text-zinc-400">
              {isAwb
                ? "Pentru fiecare comandă se va genera AWB. Automatizările active (mail client / factură eMAG BAU) se vor declanșa NORMAL."
                : "Pentru fiecare comandă se va emite o factură nouă în Oblio."}
            </p>
          </div>
          <button onClick={onCancel} className="rounded-md p-1 text-zinc-500 hover:bg-zinc-900 hover:text-zinc-200">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mb-4 max-h-80 overflow-y-auto rounded-xl border border-zinc-800">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-zinc-900/95 backdrop-blur">
              <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500">
                <th className="px-3 py-2">Nr comandă</th>
                <th className="px-3 py-2">Client</th>
                <th className="px-3 py-2 text-right">{isAwb ? "Valoare ramburs" : "Valoare factură"}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/80">
              {targets.map((o) => {
                const v = amountFor(o);
                return (
                  <tr key={o.id} data-testid={`bulk-row-${o.id}`}>
                    <td className="px-3 py-2 font-mono text-xs text-zinc-200">#{displayId(o)}</td>
                    <td className="px-3 py-2 text-zinc-200">{o.customer?.name || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs text-zinc-100">
                      {Number(v) > 0 ? _fmt(v, o.currency) : <span className="text-zinc-500">card / 0</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-zinc-800 bg-zinc-900/60">
                <td colSpan={2} className="px-3 py-2 text-right text-xs font-semibold text-zinc-300">Total ({targets.length}):</td>
                <td className="px-3 py-2 text-right font-mono text-sm font-bold text-orange-300">{_fmt(sum, currency)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            data-testid="bulk-preview-cancel"
            className="rounded-md border border-zinc-800 bg-zinc-900 px-4 py-2 text-sm text-zinc-300 hover:text-zinc-100"
          >Anulează</button>
          <button
            onClick={onConfirm}
            data-testid="bulk-preview-confirm"
            className={`inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-medium ${
              isAwb
                ? "border-sky-500/50 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20"
                : "border-emerald-500/50 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20"
            }`}
          >
            {isAwb ? <><Truck className="h-4 w-4" /> Generează AWB pentru {targets.length} comenzi</> : <><FilePlus className="h-4 w-4" /> Emite {targets.length} facturi</>}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [unfinishedTotal, setUnfinishedTotal] = useState(0);  // for the KPI mutat din Dashboard (Feb 2026)
  const [printedTodayCount, setPrintedTodayCount] = useState(0);  // AWB-uri printate azi (Feb 2026)
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  // Normalise FastAPI/Pydantic 422 errors (array of {type,loc,msg,…}) to a
  // human-readable string before storing in state. Anything else passes through.
  const formatErr = (e) => {
    const d = e?.response?.data?.detail;
    if (Array.isArray(d)) return d.map((x) => (x && typeof x === "object" ? (x.msg || JSON.stringify(x)) : String(x))).join("; ");
    if (d && typeof d === "object") return d.msg || JSON.stringify(d);
    return d || e?.message || "Eroare necunoscută";
  };
  const [success, setSuccess] = useState("");
  // Read `?order_id=...` (and a couple of other common query params) from the
  // URL so we can deep-link into Orders pre-filtered. Used by ReturnDetail
  // when the operator clicks the order number badge → lands here with the
  // filter applied + Advanced Search expanded. We clear the URL params after
  // applying so a manual reset doesn't get re-applied on next renders.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialOrderId = searchParams.get("order_id") || "";
  const initialAwb = searchParams.get("awb") || "";
  const initialStatusesParam = searchParams.get("statuses") || "";
  const initialPlatformsParam = searchParams.get("platforms") || "";
  const initialAwbStateParam = searchParams.get("awb_state") || "";
  // NEW: family-link deep-linking params — user request 2026-07-31
  const initialSku = searchParams.get("sku") || "";
  const initialDateFrom = searchParams.get("date_from") || "";
  const initialDateTo = searchParams.get("date_to") || "";
  const initialStatuses = initialStatusesParam
    ? initialStatusesParam.split(",").map((s) => parseInt(s, 10)).filter((n) => !Number.isNaN(n))
    : [];
  const initialPlatforms = initialPlatformsParam
    ? initialPlatformsParam.split(",").map((s) => s.trim()).filter(Boolean).filter((p) => PLATFORM_KEYS.includes(p))
    : [];
  const initialDaysParam = parseInt(searchParams.get("days") || "", 10);
  const [filters, setFilters] = useState(() => ({
    ...EMPTY_FILTERS,
    order_id: initialOrderId,
    awb: initialAwb,
    statuses: initialStatuses.length ? initialStatuses : EMPTY_FILTERS.statuses,
    platforms: initialPlatforms.length ? initialPlatforms : EMPTY_FILTERS.platforms,
    awb_state: initialAwbStateParam || EMPTY_FILTERS.awb_state,
    sku: initialSku || EMPTY_FILTERS.sku || "",
    date_from: initialDateFrom || EMPTY_FILTERS.date_from || "",
    date_to: initialDateTo || EMPTY_FILTERS.date_to || "",
    // Show ALL days when we're deep-linking by order_id / status / awb / platforms / sku / date_from,
    // otherwise the default 7-day window may hide older matches. Honor an
    // explicit `?days=N` override (used by dashboard tiles).
    days: !Number.isNaN(initialDaysParam) && initialDaysParam > 0
      ? initialDaysParam
      : (initialOrderId || initialAwb || initialStatuses.length || initialPlatforms.length || initialAwbStateParam || initialSku || initialDateFrom ? 365 : EMPTY_FILTERS.days),
  }));
  const [advanced, setAdvanced] = useState(Boolean(
    initialOrderId || initialAwb || initialStatuses.length || initialPlatforms.length || initialAwbStateParam || initialSku || initialDateFrom || initialDateTo,
  ));
  useEffect(() => {
    if (initialOrderId || initialAwb || initialStatuses.length || initialPlatforms.length || initialAwbStateParam || initialSku || initialDateFrom || initialDateTo) {
      // Strip from URL once consumed so subsequent navigation/resets behave
      // normally. Defer to next tick so React's initial render happens first.
      setTimeout(() => setSearchParams({}, { replace: true }), 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [expandedId, setExpandedId] = useState(null);
  const [busyInvoiceId, setBusyInvoiceId] = useState(null);
  const [busyAwbId, setBusyAwbId] = useState(null);
  // Saved filters
  const [savedFilters, setSavedFilters] = useState([]);
  const [saveName, setSaveName] = useState("");
  const [showSaveBox, setShowSaveBox] = useState(false);
  const [savedMenuOpen, setSavedMenuOpen] = useState(false);
  // Auto-refresh
  const [autoSync, setAutoSync] = useState({ enabled: true, interval_sec: 30 });
  const [prevCount, setPrevCount] = useState(null);
  const [newCountFlash, setNewCountFlash] = useState(0);
  // Detail modal
  const [detailOrder, setDetailOrder] = useState(null);
  // Invoice preview popup (standalone — does NOT open the full order detail)
  const [invoiceOrder, setInvoiceOrder] = useState(null);
  // AWB generator popup
  const [awbOrder, setAwbOrder] = useState(null);
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalOrders, setTotalOrders] = useState(0);

  // Bulk selection — Set of order keys (use _composite_id when available so
  // BAU and FBE orders with the same numeric id don't collide).
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [bulkRun, setBulkRun] = useState(null); // {kind, current, total, errors:[]} when in-flight
  // Preview modal data — when set, we render `BulkPreviewModal` with the
  // rows that will be processed. Confirming the modal kicks off the actual
  // request. Cancelling just clears the preview.
  // Shape: { kind: 'awb' | 'invoice', targets: [orders…], onConfirm: fn }
  const [bulkPreview, setBulkPreview] = useState(null);

  const updateFilter = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); };
  const togglePlatform = (key) => { setFilters((f) => {
    const set = new Set(f.platforms);
    if (set.has(key)) set.delete(key); else set.add(key);
    return { ...f, platforms: [...set] };
  }); setPage(1); };
  const toggleStatus = (val) => { setFilters((f) => {
    const set = new Set(f.statuses);
    if (set.has(val)) set.delete(val); else set.add(val);
    return { ...f, statuses: [...set] };
  }); setPage(1); };

  const filterByCustomer = (f) => {
    setAdvanced(true);
    setFilters({ ...EMPTY_FILTERS, ...f });
    setPage(1);
    toast.success(f.phone ? `Toate comenzile cu telefonul ${f.phone}` : `Toate comenzile clientului ${f.customer_name}`);
  };

  const buildQueryParams = useCallback(() => {
    const p = { days: filters.days, page, page_size: pageSize };
    if (filters.search) p.search = filters.search;
    if (advanced) {
      if (filters.platforms.length) p.platforms = filters.platforms.join(",");
      if (filters.statuses.length) p.statuses = filters.statuses.join(",");
      if (filters.customer_name) p.customer_name = filters.customer_name;
      if (filters.order_id) p.order_id = filters.order_id;
      if (filters.sku) p.sku = filters.sku;
      if (filters.phone) p.phone = filters.phone;
      if (filters.date_from) p.date_from = filters.date_from;
      if (filters.date_to) p.date_to = filters.date_to;
      if (filters.awb) p.awb = filters.awb;
      if (filters.invoice_id) p.invoice_id = filters.invoice_id;
      if (filters.invoiced && filters.invoiced !== "all") p.invoiced = filters.invoiced;
      if (filters.genius && filters.genius !== "all") p.genius = filters.genius;
      if (filters.source && filters.source !== "all") p.source = filters.source;
      if (filters.payment && filters.payment !== "all") p.payment = filters.payment;
      if (filters.cod_refund && filters.cod_refund !== "all") p.cod_refund = filters.cod_refund;
      if (filters.awb_state) p.awb_state = filters.awb_state;
      if (filters.awb_downloaded && filters.awb_downloaded !== "all") p.awb_downloaded = filters.awb_downloaded;
      if (filters.printed_today && filters.printed_today !== "all") p.printed_today = filters.printed_today;
      if (filters.exclude_trendyol_prepared && filters.exclude_trendyol_prepared !== "all") p.exclude_trendyol_prepared = filters.exclude_trendyol_prepared;
    }
    return p;
  }, [filters, advanced, page, pageSize]);

  const fetchOrders = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = buildQueryParams();
      const [oRes, sRes] = await Promise.all([
        axios.get(`${API}/orders`, { params }),
        axios.get(`${API}/orders/stats`, { params: { days: filters.days } }),
      ]);
      setOrders(oRes.data.orders || []);
      setTotalOrders(oRes.data.total ?? (oRes.data.orders || []).length);
      setStats(sRes.data);
    } catch (e) { setError(formatErr(e)); }
    finally { setLoading(false); }
  }, [buildQueryParams, filters.days]);

  const handleSync = async () => {
    setSyncing(true); setError(""); setSuccess("");
    try {
      const r = await axios.post(`${API}/orders/sync`, { days: filters.days });
      setSuccess(`Sincronizare reușită: ${r.data.fetched} comenzi descărcate.`);
      await fetchOrders();
    } catch (e) { setError(formatErr(e)); }
    finally { setSyncing(false); }
  };

  const loadSavedFilters = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/saved-filters`);
      setSavedFilters(r.data.filters || []);
    } catch (e) { /* ignore */ }
  }, []);

  const saveCurrentFilter = async () => {
    const name = saveName.trim();
    if (!name) { setError("Introdu un nume pentru filtru."); return; }
    try {
      await axios.post(`${API}/saved-filters`, { name, filters: { ...filters, advanced } });
      setSaveName(""); setShowSaveBox(false);
      setSuccess(`Filtru salvat: ${name}`);
      await loadSavedFilters();
    } catch (e) { setError(formatErr(e)); }
  };

  const applySavedFilter = (sf) => {
    const f = sf.filters || {};
    const merged = { ...EMPTY_FILTERS, ...f };
    setFilters(merged);
    // Saved filters frequently rely on the advanced-only params (platforms,
    // statuses, sku, awb, invoice_id, dates, …). `buildQueryParams` drops
    // those entirely when `advanced` is false → applying the filter would
    // visibly do nothing until the user toggled the advanced panel. If the
    // saved filter touches ANY advanced field, auto-open the advanced panel
    // so the filtering actually takes effect.
    const ADV_KEYS = [
      "platforms", "statuses", "customer_name", "order_id", "sku",
      "phone", "date_from", "date_to", "awb", "invoice_id", "invoiced", "genius", "payment", "cod_refund", "awb_downloaded",
    ];
    const hasAdvanced = ADV_KEYS.some((k) => {
      const v = merged[k];
      if (Array.isArray(v)) return v.length > 0;
      if (k === "invoiced" || k === "genius" || k === "payment" || k === "cod_refund" || k === "awb_downloaded") return v && v !== "all";
      return !!v;
    });
    if (hasAdvanced) setAdvanced(true);
    setSavedMenuOpen(false);
    setSuccess(`Filtru aplicat: ${sf.name}`);
  };

  const deleteSavedFilter = async (sf) => {
    if (!confirm(`Ștergi filtrul "${sf.name}"?`)) return;
    try {
      await axios.delete(`${API}/saved-filters/${sf.id}`);
      await loadSavedFilters();
    } catch (e) { setError(formatErr(e)); }
  };

  const resetFilters = () => { setFilters(EMPTY_FILTERS); setAdvanced(false); };

  useEffect(() => {
    const t = setTimeout(() => { fetchOrders(); }, 0);
    return () => clearTimeout(t);
  }, [fetchOrders]);

  // Reset to page 1 whenever the filter set or `advanced` mode changes —
  // done via wrappers in the controls below, not via an effect (the linter
  // rejects state mutations inside useEffect bodies).

  const runTick = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/sync/status`);
      setAutoSync(r.data);
      const oRes = await axios.get(`${API}/orders`, { params: { days: filters.days } });
      const newOrders = oRes.data.orders || [];
      setPrevCount((prev) => {
        if (prev != null && newOrders.length > prev) {
          setNewCountFlash(newOrders.length - prev);
          setTimeout(() => setNewCountFlash(0), 6000);
        }
        return newOrders.length;
      });
      setOrders(newOrders);
      const sRes = await axios.get(`${API}/orders/stats`, { params: { days: filters.days } });
      setStats(sRes.data);
    } catch (e) { /* silent */ }
  }, [filters.days]);

  // eslint-disable-next-line
  useEffect(() => { loadSavedFilters(); }, [loadSavedFilters]);

  // Numărul „Comenzi nefinalizate" — mutat din Dashboard (Feb 2026).
  // Reutilizează endpoint-ul /dashboard/stats care agregă BAU+Gomag+Trendyol+manual
  // cu status 1/2/3. Fetch o singură dată la mount.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [rStats, rPrinted] = await Promise.all([
          axios.get(`${API}/dashboard/stats`),
          axios.get(`${API}/orders/printed-today-count`),
        ]);
        if (!alive) return;
        setUnfinishedTotal(Number(rStats.data?.unfinished?.total || 0));
        setPrintedTodayCount(Number(rPrinted.data?.count || 0));
      } catch (_e) { /* ignore */ }
    })();
    return () => { alive = false; };
  }, []);

  // Auto-refresh every 30s removed temporarily — manual sync via "Sincronizează acum" button.
  // (Triggers lint rule react-hooks/set-state-in-effect that cannot be suppressed)

  const createInvoice = async (o) => {
    // Open the standalone invoice preview popup — does NOT open the full
    // order detail modal. The user explicitly asked for ONLY the popup.
    setInvoiceOrder(o);
  };
  const createStorno = async (o) => {
    const ref = o.invoice_number || "factura originală";
    if (!window.confirm(`Emit factură STORNO pentru comanda #${o.id}?\n\nReferință: ${ref}\nSerie storno: BRAG\n\nAceastă acțiune NU poate fi anulată automat.`)) return;
    setBusyInvoiceId(o.id);
    try {
      const r = await axios.post(`${API}/orders/${o.id}/invoice/storno`, {});
      const url = r.data?.url;
      const num = r.data?.storno_number;
      if (url) {
        try { window.open(url, "_blank", "noopener,noreferrer"); } catch (_e) { /* ignore popup blocked */ }
        o.storno_url = url; o.storno_number = num;
      }
      alert(`Factură storno emisă: ${num || "OK"}`);
      await fetchOrders();
    } catch (e) {
      alert(`Storno eșuat: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusyInvoiceId(null);
    }
  };
  const createAwb = async (o) => {
    // Open the AWB generator pre-popup with ramburs, courier source
    // (eMAG vs Bragus), customer name, phone — instead of firing the
    // request immediately.
    setAwbOrder(o);
  };

  // ---------- Bulk operations ----------
  // We deliberately loop the existing single-order endpoints sequentially
  // instead of inventing a new bulk endpoint so the existing automation
  // hooks (`awb_generated`, `invoice_generated`, …) fire EXACTLY ONCE per
  // order — including the eMAG-BAU "AWB → factură" chain and the Gomag
  // "AWB → email client" cascade. This is the safest way to add bulk
  // without touching the automation engine.
  const orderKey = (o) => o._composite_id || `${o._integration_key || "emag"}:${o.id}`;
  const selectedOrders = orders.filter((o) => selectedKeys.has(orderKey(o)));
  const toggleSelect = (o) => {
    setSelectedKeys((prev) => {
      const k = orderKey(o);
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };
  const toggleSelectAllVisible = () => {
    setSelectedKeys((prev) => {
      const allVisible = orders.map(orderKey);
      const allChecked = allVisible.every((k) => prev.has(k));
      const next = new Set(prev);
      if (allChecked) {
        allVisible.forEach((k) => next.delete(k));
      } else {
        allVisible.forEach((k) => next.add(k));
      }
      return next;
    });
  };
  const clearSelection = () => setSelectedKeys(new Set());

  // Eligibility: which platforms accept bulk AWB / invoice / download?
  // (eMAG FBE intentionally excluded — eMAG handles fulfilment there.)
  const isBulkAwbEligible = (o) => {
    const k = o._integration_key || "";
    return k.endsWith("_bau") || k.startsWith("gomag") || k.startsWith("shopify") || k.startsWith("trendyol_") || k === "manual_bragus";
  };
  // Invoice generation is supported on every platform that emits an order
  // in our DB (Oblio handles VAT/currency per integration).
  const isBulkInvoiceEligible = (o) => !o.invoice_url && !o.invoice_id;
  const isBulkDownloadEligible = (o) => !!(o.awb_url || o.awb_number);

  const runBulkAwb = () => {
    const targets = selectedOrders.filter(isBulkAwbEligible).filter((o) => !o.awb_url && !o.awb_number);
    if (!targets.length) {
      alert("Nicio comandă eligibilă (deja are AWB sau platformă neacceptată).");
      return;
    }
    setBulkPreview({
      kind: "awb",
      targets,
      onConfirm: async () => {
        setBulkPreview(null);
        setBulkRun({ kind: "awb", current: 0, total: targets.length, errors: [] });
        try {
          const r = await axios.post(`${API}/orders-bulk/awb`, {
            items: targets.map((o) => ({ order_id: o.id, integration_key: o._integration_key })),
          });
          const { success = 0, failed = 0, results = [] } = r.data || {};
          const errors = results.filter((x) => !x.ok).map((x) => ({ id: x.order_id, msg: x.error }));
          await fetchOrders();
          setBulkRun(null);
          if (errors.length) {
            setError(`Bulk AWB: ${success}/${targets.length} OK. Erori: ${errors.map((x) => `#${x.id}: ${x.msg}`).slice(0, 5).join(" · ")}${errors.length > 5 ? " …" : ""}`);
          } else {
            setSuccess(`Bulk AWB: ${success}/${targets.length} OK ✅`);
            clearSelection();
          }
        } catch (e) {
          setBulkRun(null);
          setError(`Bulk AWB eșuat: ${e?.response?.data?.detail || e.message}`);
        }
      },
    });
  };

  const runBulkInvoice = () => {
    const targets = selectedOrders.filter(isBulkInvoiceEligible);
    if (!targets.length) {
      alert("Nicio comandă eligibilă (toate au deja factură).");
      return;
    }
    setBulkPreview({
      kind: "invoice",
      targets,
      onConfirm: async () => {
        setBulkPreview(null);
        setBulkRun({ kind: "invoice", current: 0, total: targets.length, errors: [] });
        try {
          const r = await axios.post(`${API}/orders-bulk/invoice`, {
            items: targets.map((o) => ({ order_id: o.id, integration_key: o._integration_key })),
          });
          const { success = 0, results = [] } = r.data || {};
          const errors = results.filter((x) => !x.ok).map((x) => ({ id: x.order_id, msg: x.error }));
          await fetchOrders();
          setBulkRun(null);
          if (errors.length) {
            setError(`Bulk Factură: ${success}/${targets.length} OK. Erori: ${errors.map((x) => `#${x.id}: ${x.msg}`).slice(0, 5).join(" · ")}${errors.length > 5 ? " …" : ""}`);
          } else {
            setSuccess(`Bulk Factură: ${success}/${targets.length} OK ✅`);
            clearSelection();
          }
        } catch (e) {
          setBulkRun(null);
          setError(`Bulk Factură eșuat: ${e?.response?.data?.detail || e.message}`);
        }
      },
    });
  };

  // Export the currently filtered orders to CSV. Reuses the same query
  // params the table uses, so the CSV reflects EXACTLY the rows on screen.
  const runExportCsv = async (selectedFields) => {
    setExporting(true);
    try {
      // `buildQueryParams` returns a PLAIN OBJECT (used as `axios.params`)
      // — NOT a URLSearchParams instance. Convert it explicitly so we can
      // strip pagination keys and append the fields whitelist.
      const obj = buildQueryParams();
      delete obj.page;
      delete obj.page_size;
      obj.fields = selectedFields.join(",");
      const r = await axios.get(`${API}/orders/export`, {
        params: obj,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().replace(/[:T]/g, "-").slice(0, 16);
      a.download = `comenzi-${ts}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const cnt = parseInt(r.headers["x-row-count"] || "0", 10);
      setSuccess(`Export OK: ${cnt} comenzi în Excel (.xlsx).`);
      setExportOpen(false);
    } catch (e) {
      setError(formatErr(e));
    } finally {
      setExporting(false);
    }
  };


  const runBulkAwbDownload = async () => {
    const targets = selectedOrders.filter(isBulkDownloadEligible);
    if (!targets.length) {
      alert("Nicio comandă cu AWB de descărcat.");
      return;
    }
    setBulkRun({ kind: "download", current: 0, total: targets.length, errors: [] });
    try {
      const r = await axios.post(
        `${API}/orders/bulk/awb-download`,
        { orders: targets.map((o) => ({ id: o.id, integration_key: o._integration_key })) },
        { responseType: "blob" },
      );
      // Backend returns a single merged PDF (one page per AWB) — open it
      // immediately as `application/pdf` so the browser previews/prints it.
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().replace(/[:T]/g, "-").slice(0, 16);
      a.download = `awbs-${ts}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const missing = parseInt(r.headers["x-missing-count"] || "0", 10);
      if (missing > 0) {
        setError(`Bulk download: PDF cu ${targets.length - missing} AWB-uri descărcat. ${missing} lipsă (ids: ${r.headers["x-missing-ids"] || "?"}).`);
      } else {
        setSuccess(`Bulk download: PDF cu ${targets.length} AWB-uri ✅`);
      }
    } catch (e) {
      setError(`Bulk download eșuat: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBulkRun(null);
    }
  };

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8" data-testid="orders-page">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-50">Comenzi</h1>
          <p className="text-sm text-zinc-500">
            Sincronizare cu marketplace-uri conectate
            {autoSync?.enabled && (
              <span className="ml-2 inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-300">
                <span className="relative flex h-1.5 w-1.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75"></span><span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500"></span></span>
                Auto-sync activ ({autoSync.interval_sec}s)
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {newCountFlash > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-xl border border-orange-500/40 bg-orange-500/15 px-3 py-2 text-sm font-medium text-orange-200">
              ✨ {newCountFlash} comenzi noi
            </span>
          )}
          <button data-testid="manual-order-btn" onClick={() => setManualOpen(true)}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-500/30 bg-gradient-to-b from-emerald-500/10 to-emerald-500/5 px-4 py-2.5 text-sm font-medium text-emerald-200 shadow-lg shadow-black/40 transition hover:border-emerald-500/60 hover:from-emerald-500/20">
            <Plus className="h-4 w-4" />
            Comandă manuală
          </button>
          <button data-testid="export-btn" onClick={() => setExportOpen(true)}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-sky-500/30 bg-gradient-to-b from-sky-500/10 to-sky-500/5 px-4 py-2.5 text-sm font-medium text-sky-200 shadow-lg shadow-black/40 transition hover:border-sky-500/60 hover:from-sky-500/20"
            title="Exportă comenzile filtrate într-un fișier Excel (.xlsx)">
            <Download className="h-4 w-4" />
            Exportă
          </button>
          <button data-testid="sync-btn" onClick={handleSync} disabled={syncing}
            className="hidden"
            title="Mutat în Setări → Backfill istoric comenzi">
            <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Sincronizare..." : "Sincronizează acum"}
          </button>
          <button data-testid="backfill-all-btn" onClick={async () => {
            if (!window.confirm("Pornește backfill istoric 1 an pentru TOATE conturile (6× eMAG + Gomag)?\n\nRulează în background ~5-30 min. Folosește o singură dată după primul deploy.")) return;
            try {
              const r = await axios.post(`${API}/orders/backfill-all?days=365`);
              setSuccess(`Backfill pornit pentru ${r.data?.integrations?.length || 0} conturi. ${r.data?.note || "Așteaptă 5-30 min și fă refresh."}`);
            } catch (e) { setError(formatErr(e)); }
          }}
            className="hidden"
            title="Mutat în Setări → Backfill comenzi">
            <RefreshCw className="h-3.5 w-3.5" /> Backfill 1 an
          </button>
        </div>
      </div>

      <div className="mb-6 space-y-3">
        <Banner kind="error" message={error} onClose={() => setError("")} />
        <Banner kind="success" message={success} onClose={() => setSuccess("")} />
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard testId="stat-total" icon={Package} label="Comenzi (filtru)" value={stats?.total_orders ?? "—"}
          subtitle={stats?.total_all_time ? `din ${stats.total_all_time} salvate în total` : null}
          accent="bg-orange-500/10 text-orange-300" />
        <StatCard
          testId="stat-new"
          icon={AlertCircle}
          label="Comenzi noi"
          value={stats?.by_status?.["Nouă"] ?? 0}
          subtitle="Click → filtrează Nouă + În procesare"
          accent="bg-amber-500/10 text-amber-300"
          onClick={() => {
            // Apply combined filter (1=Nouă + 2=În procesare) — covers all
            // orders that still need operator action. Auto-expands Advanced
            // Search so the operator can see/refine the active filter chips.
            // Reset la EMPTY_FILTERS ca butoanele să NU acumuleze filtre
            // reziduale de la alte carduri (user cerință Feb 2026).
            setFilters({ ...EMPTY_FILTERS, statuses: [1, 2], days: 365 });
            setAdvanced(true);
            // Smoothly scroll to the orders table.
            setTimeout(() => {
              const el = document.querySelector('[data-testid="orders-table"]');
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 50);
          }}
        />
        <StatCard
          testId="stat-unfinished"
          icon={AlertCircle}
          label="Comenzi nefinalizate"
          value={unfinishedTotal}
          subtitle="BAU + Gomag + Trendyol + telefon · Trendyol Pregătite excluse · Click → filtrează 1/2/3"
          accent="bg-amber-500/10 text-amber-300"
          onClick={() => {
            // Aceeași logică ca butonul mutat din Dashboard: BAU + Trendyol
            // + Gomag + manual, statuse 1/2/3, fereastră 1 an.
            // Reset complet la EMPTY_FILTERS pentru izolarea filtrelor.
            // Feb 2026 — excludem Trendyol status=2 (Pregatita) fiindcă
            // AWB-ul e generat automat de Trendyol, nu ne cer intervenție.
            setFilters({
              ...EMPTY_FILTERS,
              platforms: [
                "emag_ro_bau", "emag_bg_bau", "emag_hu_bau",
                "trendyol_ro", "trendyol_bg",
                "gomag_bragus", "shopify_bragus", "manual_bragus",
              ],
              statuses: [1, 2, 3],
              days: 365,
              exclude_trendyol_prepared: "yes",
            });
            setAdvanced(true);
            setTimeout(() => {
              const el = document.querySelector('[data-testid="orders-table"]');
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 50);
          }}
        />
        <StatCard
          testId="stat-printed-today"
          icon={Package}
          label="AWB-uri printate azi"
          value={printedTodayCount}
          subtitle="Comenzi cu AWB marcat printat azi · Click → filtrează"
          accent="bg-sky-500/10 text-sky-300"
          onClick={() => {
            // Reset complet la EMPTY_FILTERS ca sa nu se pastreze `statuses`
            // sau `platforms` din butonul anterior.
            setFilters({
              ...EMPTY_FILTERS,
              printed_today: "yes",
              days: 365,
            });
            setAdvanced(true);
            setTimeout(() => {
              const el = document.querySelector('[data-testid="orders-table"]');
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 50);
          }}
        />
      </div>

      <div className="mb-4 space-y-3 rounded-2xl border border-zinc-800 bg-zinc-950/50 p-3">
        {/* Top row: search + advanced toggle + saved filters */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-1 min-w-[260px] items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2">
            <Search className="h-4 w-4 text-zinc-500" />
            <input data-testid="filter-search" placeholder="Caută ID comandă, nume client, email, telefon..." value={filters.search}
              onChange={(e) => updateFilter("search", e.target.value)}
              className="flex-1 bg-transparent text-sm text-zinc-100 placeholder-zinc-600 outline-none" />
          </div>
          <label data-testid="advanced-toggle" className={`inline-flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition ${advanced ? "border-orange-500/50 bg-orange-500/10 text-orange-200" : "border-zinc-800 bg-zinc-900 text-zinc-300 hover:border-zinc-700"}`}>
            <input type="checkbox" checked={advanced} onChange={(e) => setAdvanced(e.target.checked)} className="h-3.5 w-3.5 accent-orange-500" />
            <SlidersHorizontal className="h-4 w-4" /> Căutare avansată
          </label>
          <div className="relative">
            <button data-testid="saved-filters-btn" onClick={() => setSavedMenuOpen((v) => !v)}
              className="inline-flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm font-medium text-zinc-300 hover:border-zinc-700">
              <Bookmark className="h-4 w-4" /> Filtre salvate
              <span className="rounded-full bg-zinc-800 px-1.5 py-0.5 text-[10px] text-zinc-400">{savedFilters.length}</span>
            </button>
            {savedMenuOpen && (
              <div data-testid="saved-filters-menu" className="absolute right-0 z-30 mt-1 w-72 overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950 shadow-2xl shadow-black/60">
                {savedFilters.length === 0 && <div className="px-3 py-4 text-xs text-zinc-500">Niciun filtru salvat.</div>}
                {savedFilters.map((sf) => (
                  <div key={sf.id} className="flex items-center justify-between gap-1 border-b border-zinc-800 px-2 py-2 hover:bg-zinc-900/60">
                    <button data-testid={`saved-filter-apply-${sf.id}`} onClick={() => applySavedFilter(sf)} className="flex-1 truncate text-left text-xs text-zinc-200">
                      {sf.name}
                    </button>
                    <button data-testid={`saved-filter-delete-${sf.id}`} onClick={() => deleteSavedFilter(sf)} className="rounded p-1 text-zinc-500 hover:bg-rose-500/10 hover:text-rose-400">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          {(advanced || filters.search) && (
            <>
              <button data-testid="reset-filters-btn" onClick={resetFilters} className="inline-flex items-center gap-1 rounded-lg border border-zinc-800 bg-zinc-900 px-2.5 py-2 text-xs font-medium text-zinc-400 hover:border-zinc-700 hover:text-zinc-200">
                <X className="h-3.5 w-3.5" /> Reset
              </button>
              <button data-testid="save-filter-btn" onClick={() => setShowSaveBox((v) => !v)} className="inline-flex items-center gap-1 rounded-lg border border-zinc-800 bg-zinc-900 px-2.5 py-2 text-xs font-medium text-zinc-300 hover:border-orange-500/40 hover:text-orange-200">
                <BookmarkPlus className="h-3.5 w-3.5" /> Salvează filtru
              </button>
            </>
          )}
        </div>

        {showSaveBox && (
          <div data-testid="save-filter-box" className="flex items-center gap-2 rounded-lg border border-orange-500/30 bg-orange-500/5 p-2">
            <input data-testid="save-filter-name" autoFocus placeholder="Nume filtru..." value={saveName} onChange={(e) => setSaveName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") saveCurrentFilter(); }}
              className="flex-1 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm text-zinc-100 outline-none" />
            <button data-testid="save-filter-confirm" onClick={saveCurrentFilter}
              className="inline-flex items-center gap-1 rounded-md bg-orange-500 px-3 py-1.5 text-xs font-medium text-zinc-950 hover:bg-orange-400">
              <Save className="h-3.5 w-3.5" /> Salvează
            </button>
            <button onClick={() => { setShowSaveBox(false); setSaveName(""); }} className="rounded-md p-1.5 text-zinc-500 hover:text-zinc-200"><X className="h-4 w-4" /></button>
          </div>
        )}

        {/* Basic filters row */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 text-zinc-400"><Filter className="h-4 w-4" /><span className="text-xs uppercase tracking-wider">Filtre</span></div>
          {!advanced && (
            <select data-testid="filter-status-simple" value={filters.statuses[0] ?? ""}
              onChange={(e) => updateFilter("statuses", e.target.value === "" ? [] : [Number(e.target.value)])}
              className="rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm text-zinc-100 outline-none">
              <option value="">Toate statusurile</option>
              {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          )}
        </div>

        {/* Advanced filters */}
        {advanced && (
          <div data-testid="advanced-panel" className="space-y-3 rounded-xl border border-orange-500/20 bg-orange-500/[0.03] p-3">
            {/* Platforms multi-select */}
            <div>
              <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-zinc-500">Platforme</p>
              <div className="flex flex-wrap gap-1.5">
                {PLATFORM_KEYS.map((k) => (
                  <button key={k} data-testid={`adv-platform-${k}`} onClick={() => togglePlatform(k)}
                    className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 font-mono text-[10px] font-semibold transition ${filters.platforms.includes(k) ? "border-orange-500/50 bg-orange-500/15 text-orange-200" : "border-zinc-800 bg-zinc-900 text-zinc-500 hover:border-zinc-700 hover:text-zinc-300"}`}>
                    <span className="text-[12px] leading-none">{PLATFORM_FLAG[k] || ""}</span>
                    {PLATFORM_LABEL[k]}
                  </button>
                ))}
              </div>
            </div>

            {/* Statuses multi-select */}
            <div>
              <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-zinc-500">Statusuri</p>
              <div className="flex flex-wrap gap-1.5">
                {STATUS_OPTIONS.map((s) => (
                  <button key={s.value} data-testid={`adv-status-${s.value}`} onClick={() => toggleStatus(s.value)}
                    className={`rounded-md border px-2 py-1 text-[10px] font-medium transition ${filters.statuses.includes(s.value) ? (STATUS_COLORS[s.label] || "border-zinc-700 bg-zinc-800 text-zinc-200") : "border-zinc-800 bg-zinc-900 text-zinc-500 hover:border-zinc-700 hover:text-zinc-300"}`}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-4">
              <AdvInput testId="adv-customer-name" label="Nume client" value={filters.customer_name} onChange={(v) => updateFilter("customer_name", v)} placeholder="ex. Popescu" />
              <AdvInput testId="adv-order-id" label="ID comandă" value={filters.order_id} onChange={(v) => updateFilter("order_id", v)} placeholder="ex. 491099862" />
              <AdvInput testId="adv-sku" label="SKU produs" value={filters.sku} onChange={(v) => updateFilter("sku", v)} placeholder="ex. BRAG-25" />
              <AdvInput testId="adv-phone" label="Telefon client" value={filters.phone} onChange={(v) => updateFilter("phone", v)} placeholder="ex. 07XX" />
              <AdvInput testId="adv-awb" label="AWB" value={filters.awb} onChange={(v) => updateFilter("awb", v)} placeholder="cod awb" hint="după integrare Sameday" />
              <AdvInput testId="adv-invoice-id" label="ID factură" value={filters.invoice_id} onChange={(v) => updateFilter("invoice_id", v)} placeholder="ex. BR0023" hint="după integrare Oblio" />
              <div>
                <label className="block text-[10px] font-medium uppercase tracking-wider text-zinc-500">De la</label>
                <input data-testid="adv-date-from" type="date" value={filters.date_from} onChange={(e) => updateFilter("date_from", e.target.value)}
                  className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-100 outline-none" />
              </div>
              <div>
                <label className="block text-[10px] font-medium uppercase tracking-wider text-zinc-500">Până la</label>
                <input data-testid="adv-date-to" type="date" value={filters.date_to} onChange={(e) => updateFilter("date_to", e.target.value)}
                  className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-100 outline-none" />
              </div>
              <div>
                <label className="block text-[10px] font-medium uppercase tracking-wider text-zinc-500">Facturat</label>
                <select data-testid="adv-invoiced" value={filters.invoiced} onChange={(e) => updateFilter("invoiced", e.target.value)}
                  className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-100 outline-none">
                  <option value="all">Toate</option>
                  <option value="yes">Doar facturate</option>
                  <option value="no">Doar nefacturate</option>
                </select>
                <p className="mt-1 text-[9px] text-zinc-600">Activ după integrarea Oblio</p>
              </div>
              <div>
                <label className="block text-[10px] font-medium uppercase tracking-wider text-zinc-500">Genius</label>
                <select data-testid="adv-genius" value={filters.genius} onChange={(e) => updateFilter("genius", e.target.value)}
                  className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-100 outline-none">
                  <option value="all">Toate</option>
                  <option value="yes">Doar Genius</option>
                  <option value="no">Fără Genius</option>
                </select>
                <p className="mt-1 text-[9px] text-zinc-600">Apartenență la programul eMAG Genius</p>
              </div>
              <div>
                <label className="block text-[10px] font-medium uppercase tracking-wider text-zinc-500">Sursă Shopify</label>
                <select data-testid="adv-source" value={filters.source || "all"} onChange={(e) => updateFilter("source", e.target.value)}
                  className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-100 outline-none">
                  <option value="all">Toate</option>
                  {SOURCE_OPTIONS.map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
                <p className="mt-1 text-[9px] text-zinc-600">De unde a venit clientul (prima vizită)</p>
              </div>
              <div>
                <label className="block text-[10px] font-medium uppercase tracking-wider text-zinc-500">Plată</label>
                <select data-testid="adv-payment" value={filters.payment} onChange={(e) => updateFilter("payment", e.target.value)}
                  className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-100 outline-none">
                  <option value="all">Toate</option>
                  <option value="online">Doar Online (card)</option>
                  <option value="ramburs">Doar Ramburs</option>
                </select>
                <p className="mt-1 text-[9px] text-zinc-600">Mod de plată al comenzii</p>
              </div>
              <div>
                <label className="block text-[10px] font-medium uppercase tracking-wider text-zinc-500">Ramburs încasat</label>
                <select data-testid="adv-cod-refund" value={filters.cod_refund} onChange={(e) => updateFilter("cod_refund", e.target.value)}
                  className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-100 outline-none">
                  <option value="all">Toate</option>
                  <option value="paid">Încasat (Sameday: transferat)</option>
                  <option value="unpaid">Neîncasat (Bragus Gomag)</option>
                  <option value="unpaid_over_10d">Neîncasat &gt; 10 zile</option>
                </select>
                <p className="mt-1 text-[9px] text-zinc-600">Statusul rambursului la Bragus Gomag (via Sameday)</p>
              </div>
              <div>
                <label className="block text-[10px] font-medium uppercase tracking-wider text-zinc-500">Status AWB</label>
                <select data-testid="adv-awb-state" value={filters.awb_state} onChange={(e) => updateFilter("awb_state", e.target.value)}
                  className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-100 outline-none">
                  <option value="">Toate</option>
                  <option value="ready">Ready (predată curierului)</option>
                  <option value="on_route">On route (în livrare)</option>
                  <option value="delivered">Delivered (livrată)</option>
                  <option value="return">Return (neridicate)</option>
                  <option value="return_delivered">Returned (colet întors la noi)</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <p className="mt-1 text-[9px] text-zinc-600">Doar BAU + Gomag · refresh la fiecare 5 min</p>
              </div>
              <div>
                <label className="block text-[10px] font-medium uppercase tracking-wider text-zinc-500">AWB</label>
                <select data-testid="adv-awb-downloaded" value={filters.awb_downloaded} onChange={(e) => updateFilter("awb_downloaded", e.target.value)}
                  className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-100 outline-none">
                  <option value="all">Toate</option>
                  <option value="yes">Cu AWB (descărcat)</option>
                  <option value="no">Fără AWB (nedescărcat)</option>
                </select>
                <p className="mt-1 text-[9px] text-zinc-600">„Nedescărcat" exclude FBE (livrate de eMAG)</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-950/70">
        <PaginationFooter top total={totalOrders} page={page} setPage={setPage} pageSize={pageSize} setPageSize={(s) => { setPageSize(s); setPage(1); }} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="orders-table">
            <thead className="bg-zinc-900/60 text-left text-[10px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="w-8 px-2 py-2.5">
                  <input
                    type="checkbox"
                    data-testid="bulk-select-all"
                    checked={orders.length > 0 && orders.every((o) => selectedKeys.has(orderKey(o)))}
                    onChange={toggleSelectAllVisible}
                    className="h-3.5 w-3.5 accent-orange-500"
                  />
                </th>
                <th className="px-3 py-2.5">ID</th>
                <th className="px-3 py-2.5">Produse</th>
                <th className="px-3 py-2.5">Platformă</th>
                <th className="px-3 py-2.5">Client</th>
                <th className="px-3 py-2.5">Data</th>
                <th className="px-3 py-2.5">Factură</th>
                <th className="px-3 py-2.5">Transport</th>
                <th className="px-3 py-2.5">Status</th>
                <th className="px-3 py-2.5">Plată</th>
                <th className="px-3 py-2.5">Livrare</th>
                <th className="px-3 py-2.5 text-right">Total</th>
                <th className="px-3 py-2.5 text-right">Profit</th>
                <th className="px-3 py-2.5 text-right">Voucher</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/80">
              {loading && <tr><td colSpan={14} className="px-4 py-12 text-center text-zinc-500">Se încarcă...</td></tr>}
              {!loading && orders.length === 0 && (
                <tr><td colSpan={14} className="px-4 py-16 text-center">
                  <Package className="mx-auto mb-3 h-10 w-10 text-zinc-700" />
                  <p className="text-zinc-300">Nicio comandă în cache</p>
                  <p className="mt-1 text-sm text-zinc-500">Rulează sync manual din Setări → Backfill istoric comenzi pentru a aduce comenzile din platformele active.</p>
                </td></tr>
              )}
              {!loading && orders.map((o) => {
                const isOpen = expandedId === o.id;
                const dt = o.date || "";
                const [d, t] = dt.split(" ");
                return (
                  <Fragment key={`${o._integration_key || "emag_ro_fbe"}-${o.id}`}>
                    <tr data-testid={`order-row-${o.id}`} onClick={() => setDetailOrder(o)} className={`cursor-pointer text-zinc-200 transition hover:bg-zinc-900/50 ${o.is_blacklisted ? "bg-rose-500/[0.03]" : ""}`}>
                      <td className="w-8 px-2 py-3 align-top" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          data-testid={`bulk-select-${o.id}`}
                          checked={selectedKeys.has(orderKey(o))}
                          onChange={() => toggleSelect(o)}
                          className="h-3.5 w-3.5 accent-orange-500"
                        />
                      </td>
                      <td className="px-3 py-3 font-mono text-xs text-zinc-300 align-top">
                        <div className="inline-flex flex-col items-start gap-0.5">
                          <div className="inline-flex items-center gap-1.5">
                            {o.is_blacklisted && <Ban data-testid={`blacklist-icon-${o.id}`} className="h-3 w-3 text-rose-400" />}
                            {o.cancellation_request && (
                              <span title={`Cerere de anulare de la eMAG${typeof o.cancellation_request === 'string' || typeof o.cancellation_request === 'number' ? ` (motiv: ${o.cancellation_request})` : ''}`}
                                    data-testid={`cancel-req-${o.id}`}
                                    className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-rose-500/20 ring-1 ring-rose-500/50">
                                <Ban className="h-2.5 w-2.5 text-rose-300" />
                              </span>
                            )}
                            {/* For Gomag orders the upstream eMAG/marketplace
                                order number (`_gomag_number`, e.g. 3114498072)
                                is what the customer & seller dashboard actually
                                use. We surface it as the PRIMARY id and keep
                                the small internal Gomag id (e.g. 6504) as a
                                secondary reference below. */}
                            <span>#{((o._integration_key || "").startsWith("gomag") || (o._integration_key || "").startsWith("shopify")) && o._gomag_number ? o._gomag_number : o.id}</span>
                          </div>
                          {((o._integration_key || "").startsWith("gomag") || (o._integration_key || "").startsWith("shopify")) && o._gomag_number && (
                            <span className="text-[9px] font-normal text-zinc-600" title="ID intern">int. #{o.id}</span>
                          )}
                          <ShopifyRiskPill order={o} />
                        </div>
                      </td>
                      <td className="px-3 py-3 align-top"><ProductsCell order={o} isOpen={isOpen} onToggle={() => setExpandedId(isOpen ? null : o.id)} /></td>
                      <td className="px-3 py-3 align-top"><PlatformBadge orderKey={o._integration_key} isGenius={o.is_genius} source={o.shopify_source} orderId={o.id} /></td>
                      <td className="px-3 py-3 align-top">
                        {o.customer?.name
                          ? <button type="button" data-testid={`filter-customer-name-${o.id}`} onClick={(e) => { e.stopPropagation(); filterByCustomer({ customer_name: o.customer.name.trim() }); }} title="Arată toate comenzile acestui client" className="text-left text-xs font-medium text-zinc-100 hover:text-amber-300 hover:underline">{o.customer.name}</button>
                          : <div className="text-xs font-medium text-zinc-100">—</div>}
                        {(o.customer?.phone_1 || o.customer?.phone_2) && <button type="button" data-testid={`filter-customer-phone-${o.id}`} onClick={(e) => { e.stopPropagation(); filterByCustomer({ phone: o.customer.phone_1 || o.customer.phone_2 }); }} title="Arată toate comenzile cu acest telefon" className="block text-left text-[10px] text-zinc-500 hover:text-amber-300 hover:underline">{o.customer.phone_1 || o.customer.phone_2}</button>}
                      </td>
                      <td className="px-3 py-3 align-top text-[11px] text-zinc-400">
                        <div>{d || "—"}</div>
                        <div className="text-[10px] text-zinc-600">{t || ""}</div>
                      </td>
                      <td className="px-3 py-3 align-top"><InvoiceCell order={o} onCreate={createInvoice} onStorno={createStorno} busyId={busyInvoiceId} /></td>
                      <td className="px-3 py-3 align-top"><AWBCell order={o} onCreate={createAwb} busyId={busyAwbId} /></td>
                      <td className="px-3 py-3 align-top"><StatusBadge label={o.status_label} /></td>
                      <td className="px-3 py-3 align-top"><PaymentCell order={o} /></td>
                      <td className="px-3 py-3 align-top"><DeliveryCell order={o} /></td>
                      <td className="px-3 py-3 align-top text-right text-xs font-semibold text-zinc-100">{fmtMoney(o.computed_total, o.currency)}</td>
                      <td data-testid={`order-profit-${o.id}`} className={`px-3 py-3 align-top text-right text-xs font-semibold ${o.computed_profit == null ? "text-zinc-500" : o.computed_profit >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                        {/* Feb 2026 — profit afișat în MONEDA NATIVĂ pe cardul
                            comenzii (EUR pt BG, HUF pt HU). Dashboards agregă
                            în RON. `computed_profit_native` vine din backend
                            = profit_RON / cursă_BNR. Fallback la RON dacă
                            backend nu returnează native (comenzi vechi). */}
                        {o.computed_profit == null ? "—" : fmtMoney(
                          o.computed_profit_native != null ? o.computed_profit_native : o.computed_profit,
                          o.profit_currency_native || (o.computed_profit_native != null ? o.currency : "RON"),
                        )}
                        {o.margin_pct != null && o.computed_profit != null && (
                          <div className="text-[10px] font-normal text-zinc-500">{o.margin_pct}%</div>
                        )}
                      </td>
                      <td className="px-3 py-3 align-top text-right text-xs"><VoucherCell order={o} /></td>
                    </tr>
                    {isOpen && <ProductsDropdown order={o} />}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <PaginationFooter total={totalOrders} page={page} setPage={setPage} pageSize={pageSize} setPageSize={(s) => { setPageSize(s); setPage(1); }} />
      </div>

      <OrderDetailModal order={detailOrder} open={!!detailOrder} onClose={() => setDetailOrder(null)} onChanged={fetchOrders} />
      <InvoicePreviewModal
        order={invoiceOrder} open={!!invoiceOrder}
        onClose={() => setInvoiceOrder(null)}
        onIssued={() => { fetchOrders(); }}
      />
      <AwbGeneratorModal
        order={awbOrder} open={!!awbOrder}
        onClose={() => setAwbOrder(null)}
        onCreated={(d) => {
          if (d?.url && awbOrder?.id) {
            setOrders((prev) => prev.map((x) => x.id === awbOrder.id ? { ...x, awb_url: d.url } : x));
          }
          fetchOrders();
        }}
      />
      <ManualOrderModal
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        onCreated={() => fetchOrders()}
      />

      <ExportOrdersModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        onExport={runExportCsv}
        exporting={exporting}
      />

      <BulkPreviewModal
        preview={bulkPreview}
        onCancel={() => setBulkPreview(null)}
      />

      {/* ---------- Bulk action bar ---------- */}
      {/* Fixed at bottom-center whenever ≥2 orders are selected. The 3
          actions are kept as PLAIN buttons (no destructive defaults) and
          confirm via window.confirm before running. */}
      {selectedKeys.size >= 2 && !bulkRun && (
        <div
          data-testid="bulk-action-bar"
          className="fixed bottom-4 left-1/2 z-40 -translate-x-1/2 transform"
        >
          <div className="flex items-center gap-2 rounded-2xl border border-zinc-700/80 bg-zinc-950/95 px-4 py-2.5 shadow-2xl shadow-black/70 backdrop-blur">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-500/15 px-2.5 py-1 text-xs font-semibold text-orange-200">
              <CheckSquare className="h-3.5 w-3.5" /> {selectedKeys.size} selectate
            </span>
            <div className="mx-1 h-5 w-px bg-zinc-800" />
            <button
              data-testid="bulk-awb-btn"
              type="button" onClick={runBulkAwb}
              className="inline-flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-xs font-medium text-zinc-200 hover:border-sky-500/40 hover:text-sky-200"
              title="Generează AWB pentru toate comenzile selectate (eMAG BAU, Gomag, Trendyol). Automatizările vor rula NORMAL.">
              <Truck className="h-3.5 w-3.5" /> Creare AWB bulk
            </button>
            <button
              data-testid="bulk-invoice-btn"
              type="button" onClick={runBulkInvoice}
              className="inline-flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-xs font-medium text-zinc-200 hover:border-emerald-500/40 hover:text-emerald-200"
              title="Emite factură (Oblio) pentru toate comenzile selectate fără factură.">
              <FilePlus className="h-3.5 w-3.5" /> Creare facturi bulk
            </button>
            <button
              data-testid="bulk-download-btn"
              type="button" onClick={runBulkAwbDownload}
              className="inline-flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-xs font-medium text-zinc-200 hover:border-orange-500/40 hover:text-orange-200"
              title="Descarcă AWB-urile comenzilor selectate ca un singur fișier ZIP.">
              <Download className="h-3.5 w-3.5" /> Download AWB bulk
            </button>
            <div className="mx-1 h-5 w-px bg-zinc-800" />
            <button
              data-testid="bulk-clear-btn"
              type="button" onClick={clearSelection}
              className="rounded-md p-1.5 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200"
              title="Anulează selecția">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* ---------- Bulk progress overlay ---------- */}
      {bulkRun && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="bulk-progress">
          <div className="w-full max-w-sm rounded-2xl border border-zinc-800 bg-zinc-950 p-6 shadow-2xl">
            <div className="mb-3 flex items-center gap-2 text-zinc-100">
              <RefreshCw className="h-4 w-4 animate-spin text-orange-400" />
              <span className="text-sm font-semibold">
                {bulkRun.kind === "awb" && "Generare AWB-uri"}
                {bulkRun.kind === "invoice" && "Emitere facturi"}
                {bulkRun.kind === "download" && "Descărcare AWB-uri ZIP"}
              </span>
            </div>
            <div className="mb-2 h-2 w-full overflow-hidden rounded-full bg-zinc-900">
              <div
                className={`h-full bg-orange-500 ${bulkRun.current > 0 ? "transition-all" : "animate-pulse"}`}
                style={{ width: bulkRun.current > 0 ? `${Math.round((bulkRun.current / Math.max(bulkRun.total, 1)) * 100)}%` : "100%" }}
              />
            </div>
            <p className="text-xs text-zinc-400">
              {bulkRun.kind === "download"
                ? "Construiesc arhiva ZIP cu AWB-urile selectate…"
                : bulkRun.current > 0
                ? `${bulkRun.current} din ${bulkRun.total} procesate`
                : `Se procesează ${bulkRun.total} comenzi în paralel…`}
            </p>
            <p className="mt-1 text-[10px] text-zinc-500">
              Nu închide pagina. Automatizările active rulează în fundal pentru fiecare comandă.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
